import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { App, Switch, Typography } from 'antd';
import { DoorOpen, ArrowDown } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AssessmentInputMode,
  ConversationRole,
  StreamEventType,
  VOICE_TURN_SEPARATOR,
  type StreamEvent,
  type VoiceTurnTiming,
} from '@evaltick/shared';
import { useSessionToken, useSessionRoutes, useBreakpoint, useLiquidGlass, useIsGuest } from '@/hooks';
import { FORM_GLASS_SETTINGS } from '@/constants';
import { Logo } from '@/components/Logo';
import {
  AnswerMode,
  ConversationEntryKind,
  useInterviewStore,
  type ConversationEntry,
} from '@/store/interviewStore';
import {
  VoicePhase,
  VoiceActivity,
  POST_ANSWERING_PHASES,
  RECORDING_PHASES,
  usePageHeaderContent,
  usePageNavigationGuard,
  useRevokedKickOut,
} from '@/hooks';
import { QuestionCard } from '@/components/Interview/QuestionCard';
import { AnswerModeToggle } from '@/components/Interview/AnswerModeToggle';
import { AnswerInput } from '@/components/Interview/AnswerInput';
import { VoiceAnswerPanel } from '@/components/Interview/VoiceAnswerPanel';
import { FeedbackThread } from '@/components/Interview/FeedbackThread';
import { FollowUpInput } from '@/components/Interview/FollowUpInput';
import { ScoringIndicator } from '@/components/Interview/ScoringIndicator';
import { NextStepCard } from '@/components/Interview/NextStepCard';
import { ExitSessionModal } from '@/components/ExitSessionModal';
import { AgentService, AssessmentService, BaseAPIClient } from '@/services/api';
import { streamText, containerVariants, itemVariants } from '@/utils';
import { VoiceOrbCondition } from '@/components/Interview/VoiceOrb/condition';
import styles from './SessionInterview.module.scss';

const { Text } = Typography;

const VoiceOrb = React.lazy(() =>
  import('@/components/Interview/VoiceOrb/VoiceOrb').then((m) => ({ default: m.VoiceOrb }))
);

const VOICE_ORB_SIZE = '164px';
const VOICE_ORB_SIZE_MOBILE = '140px';

const FOLLOW_UP_DELAY_MS = 600;

const BOTTOM_THRESHOLD_PX = 24;

const FEEDBACK_AUDIO_TAIL_MS = 700;

const ANSWER_TIMER_TICK_MS = 1000;

const VOICE_WAITING_PHASES: ReadonlySet<VoicePhase> = new Set([VoicePhase.Finalizing, VoicePhase.Feedback]);

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const EXIT_BTN_GLASS_SETTINGS = { ...FORM_GLASS_SETTINGS, radius: 999 };

export const SessionInterview: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { getToken } = useSessionToken();
  const sessionRoutes = useSessionRoutes();
  const isGuest = useIsGuest();
  const { isMobile, isDesktop } = useBreakpoint();
  const exitGlassRef = useLiquidGlass<HTMLButtonElement>(EXIT_BTN_GLASS_SETTINGS);
  const { message: toast } = App.useApp();
  const [exitDestination, setExitDestination] = useState<string | null>(null);

  const [answerElapsedSec, setAnswerElapsedSec] = useState(0);
  const [showAnswerTimer, setShowAnswerTimer] = useState(false);
  const answerElapsedMsRef = useRef(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const questionCardRef = useRef<HTMLDivElement>(null);

  const [dockedVoiceSlot, setDockedVoiceSlot] = useState<HTMLDivElement | null>(null);

  const [voicePhase, setVoicePhase] = useState<VoicePhase>(VoicePhase.Idle);
  const [voiceActivity, setVoiceActivity] = useState<VoiceActivity>(VoiceActivity.Idle);
  const [voiceInputTrouble, setVoiceInputTrouble] = useState(false);
  const voiceAudioLevelRef = useRef<() => number>(() => 0);
  const getVoiceOrbAudioLevel = useCallback(() => voiceAudioLevelRef.current(), []);
  const handleAudioLevelSource = useCallback((getLevel: () => number) => {
    voiceAudioLevelRef.current = getLevel;
  }, []);
  const addVoiceSessionContextRef = useRef<(text: string) => void>(() => {});
  const handleSessionContextSink = useCallback((addSessionContext: (text: string) => void) => {
    addVoiceSessionContextRef.current = addSessionContext;
  }, []);
  const [voiceOrbReady, setVoiceOrbReady] = useState(false);
  const [voiceAnswerJustCaptured, setVoiceAnswerJustCaptured] = useState(false);
  const [feedbackAudioSettled, setFeedbackAudioSettled] = useState(true);
  const [evaluateFailed, setEvaluateFailed] = useState(false);
  const [nextCardRevealed, setNextCardRevealed] = useState(false);
  const [nextCardHeight, setNextCardHeight] = useState(0);

  const [showQuestionPeek, setShowQuestionPeek] = useState(false);
  const [isQuestionInView, setIsQuestionInView] = useState(true);

  const [isAtBottom, setIsAtBottom] = useState(true);
  const isAtBottomRef = useRef(true);
  useEffect(() => {
    isAtBottomRef.current = isAtBottom;
  }, [isAtBottom]);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const {
    assessmentId: storedAssessmentId,
    templateName,
    coachingEnabled,
    questions,
    currentQuestionIndex,
    answers,
    answeredQuestions,
    conversations,
    answerModes,
    isStreaming,
    threadIds,
    setAnswer,
    setAnswerMode,
    setConversation,
    setStreaming,
    beginStreamingEntry,
    appendStreamingChunk,
    finalizeStreamingEntry,
    appendEntry,
    markQuestionAnswered,
    setThreadId,
    advanceQuestion,
    reset,
  } = useInterviewStore();

  const sessionsHub = sessionRoutes.hub;
  useEffect(() => {
    if (!storedAssessmentId || storedAssessmentId !== assessmentId) {
      navigate(sessionsHub, { replace: true });
    }
  }, [storedAssessmentId, assessmentId, navigate, sessionsHub]);

  // Every write 410s after a revoke. Same teardown as a confirmed exit, minus
  // the confirm — the hook has already navigated away.
  const kickOutIfRevoked = useRevokedKickOut();
  const handleIfRevoked = useCallback(
    (err: unknown): boolean => {
      if (!kickOutIfRevoked(err)) return false;
      abortRef.current?.abort();
      reset();
      return true;
    },
    [kickOutIfRevoked, reset]
  );

  useEffect(() => {
    setVoicePhase(VoicePhase.Idle);
    setVoiceActivity(VoiceActivity.Idle);
    setVoiceInputTrouble(false);
    answerElapsedMsRef.current = 0;
    setVoiceAnswerJustCaptured(false);
    setEvaluateFailed(false);
    setFeedbackAudioSettled(true);
    setAnswerElapsedSec(0);
    setShowAnswerTimer(false);
    setNextCardRevealed(false);
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (VOICE_WAITING_PHASES.has(voicePhase)) {
      setFeedbackAudioSettled(false);
      return;
    }
    const timeoutId = setTimeout(() => setFeedbackAudioSettled(true), FEEDBACK_AUDIO_TAIL_MS);
    return () => clearTimeout(timeoutId);
  }, [voicePhase]);

  const cancellersRef = useRef<Array<() => void>>([]);
  const registerCanceller = useCallback((cancel: () => void) => {
    cancellersRef.current.push(cancel);
  }, []);

  useEffect(
    () => () => {
      cancellersRef.current.forEach((cancel) => cancel());
      cancellersRef.current = [];
    },
    []
  );

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestionIndex] ?? '';
  const currentConversation = useMemo(
    () => conversations[currentQuestionIndex] ?? [],
    [conversations, currentQuestionIndex]
  );
  const currentQuestionAnswered = answeredQuestions[currentQuestionIndex] ?? false;
  const currentAnswerMode = answerModes[currentQuestionIndex] ?? AnswerMode.Text;
  const isVoiceMode = currentAnswerMode === AnswerMode.Voice;

  useEffect(() => {
    if (!isVoiceMode) setVoiceOrbReady(false);
  }, [isVoiceMode]);

  useEffect(() => {
    if (!isVoiceMode || voicePhase !== VoicePhase.Answering) return;
    // Counts time spent answering, across reconnects: re-entering Answering
    // picks up from what was banked when it was last left, rather than from 0.
    const bankedMs = answerElapsedMsRef.current;
    const startMs = Date.now();
    setShowAnswerTimer(true);
    setAnswerElapsedSec(Math.floor(bankedMs / 1000));
    const id = setInterval(() => {
      setAnswerElapsedSec(Math.floor((bankedMs + Date.now() - startMs) / 1000));
    }, ANSWER_TIMER_TICK_MS);
    return () => {
      clearInterval(id);
      answerElapsedMsRef.current = bankedMs + Date.now() - startMs;
    };
  }, [voicePhase, isVoiceMode]);

  useLayoutEffect(() => {
    const el = scrollAreaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [isVoiceMode]);

  const lastConversationEntry = currentConversation[currentConversation.length - 1];
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setIsAtBottom(true);
  }, [currentConversation.length, lastConversationEntry?.content]);

  useEffect(() => {
    setIsAtBottom(true);
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (!nextCardRevealed || !isAtBottomRef.current) return;
    const el = scrollAreaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [nextCardRevealed, nextCardHeight]);

  const handleScroll = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    setIsAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX);
  }, []);

  useEffect(() => {
    const root = scrollAreaRef.current;
    const target = questionCardRef.current;
    if (!root || !target) return;
    setIsQuestionInView(true);
    const observer = new IntersectionObserver(([entry]) => setIsQuestionInView(entry.isIntersecting), {
      root,
      threshold: 0,
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [isVoiceMode]);

  useEffect(() => {
    if (isQuestionInView) setShowQuestionPeek(false);
  }, [isQuestionInView]);

  const scrollToBottom = useCallback(() => {
    const el = scrollAreaRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  const completedIndices = answeredQuestions
    .map((answered, i) => (answered ? i : -1))
    .filter((i) => i !== -1);

  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const allDone = completedIndices.length === questions.length;
  const canChat = !isVoiceMode && !isStreaming && currentQuestionAnswered && coachingEnabled;

  const coachHasReplied = currentConversation.some((e) => e.role === ConversationRole.Coach);

  const showAnswerInput = !isVoiceMode && !isStreaming && !currentQuestionAnswered && !coachHasReplied;

  const showFeedback = currentConversation.length > 0 && !showAnswerInput && !isVoiceMode;

  const voiceAnsweredPhase = POST_ANSWERING_PHASES.has(voicePhase);

  const handleNext = useCallback(async () => {
    if (!assessmentId) return;
    if (isLastQuestion || allDone) {
      try {
        const token = await getToken();
        if (!token) return;
        await new AssessmentService(token).completeAssessment(assessmentId);
      } catch (err) {
        if (handleIfRevoked(err)) return;
        console.error('completeAssessment failed:', BaseAPIClient.parseErrorMsg(err));
      }
      navigate(sessionRoutes.results(assessmentId));
    } else {
      advanceQuestion();
    }
  }, [assessmentId, isLastQuestion, allDone, getToken, navigate, advanceQuestion, sessionRoutes, handleIfRevoked]);

  const showCoachError = useCallback(
    (questionIndex: number, message: string) => {
      appendStreamingChunk(questionIndex, `\n\n⚠️ ${message}`);
      finalizeStreamingEntry(questionIndex);
    },
    [appendStreamingChunk, finalizeStreamingEntry]
  );

  const runEvaluate = useCallback(async (
    answerText: string,
    inputMode: AssessmentInputMode,
    voiceTurnTimings?: VoiceTurnTiming[]
  ) => {
    if (!currentQuestion || isStreaming || currentQuestionAnswered || !assessmentId) return;

    const narratesResult = inputMode === AssessmentInputMode.TEXT && coachingEnabled;
    if (!narratesResult) setVoiceAnswerJustCaptured(true);
    setEvaluateFailed(false);
    const reportError = (text: string) => {
      setEvaluateFailed(true);
      if (narratesResult) showCoachError(currentQuestionIndex, text);
      else {
        setStreaming(false);
        setVoiceAnswerJustCaptured(false);
        toast.error(text);
      }
    };

    setAnswer(currentQuestionIndex, answerText);
    if (inputMode === AssessmentInputMode.TEXT) {
      appendEntry(currentQuestionIndex, {
        role: ConversationRole.Candidate,
        content: answerText,
        isStreaming: false,
        occurredAtMs: Date.now(),
      });
    }

    if (narratesResult) beginStreamingEntry(currentQuestionIndex);
    else setStreaming(true);

    abortRef.current = new AbortController();

    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const service = new AgentService(token);
      let capturedThreadId: string | null = null;

      await service.streamEvaluate(
        {
          message: answerText,
          competencyId: currentQuestion.competencyId,
          questionType: currentQuestion.questionType,
          questionText: currentQuestion.text,
          assessmentId,
          interviewTemplateItemId: currentQuestion.id,
          voiceTurnTimings,
          inputMode,
        },
        {
          signal: abortRef.current.signal,
          onEvent: (event) => {
            if (event.type === StreamEventType.Complete) {
              capturedThreadId = event.threadId;
            }
          },
          onError: (err) => {
            if (handleIfRevoked(err)) return;
            reportError(err.message);
          },
          onComplete: async () => {
            if (!capturedThreadId) {
              reportError('No response received from evaluation');
              return;
            }

            try {
              const freshToken = await getToken();
              if (!freshToken) throw new Error('Not authenticated');
              const freshService = new AgentService(freshToken);
              const result = await freshService.getAssessmentResponse(capturedThreadId);

              markQuestionAnswered(currentQuestionIndex);
              setThreadId(currentQuestionIndex, capturedThreadId);

              // The per-component timing, now that scoring found the
              // boundaries — the live coach only had the total until now.
              if (inputMode === AssessmentInputMode.VOICE && result.answerLengthContext) {
                addVoiceSessionContextRef.current(result.answerLengthContext);
              }

              if (narratesResult) {
                const cancelStream = streamText(
                  result.rationale,
                  (chunk) => appendStreamingChunk(currentQuestionIndex, chunk),
                  () => finalizeStreamingEntry(currentQuestionIndex)
                );
                registerCanceller(cancelStream);
              } else {
                setStreaming(false);
              }
            } catch (fetchErr) {
              reportError(
                fetchErr instanceof Error ? fetchErr.message : 'Failed to load result'
              );
            }
          },
        }
      );
    } catch (err) {
      if (abortRef.current?.signal.aborted) return;
      if (handleIfRevoked(err)) return;
      reportError(err instanceof Error ? err.message : 'Something went wrong');
    }
  }, [
    handleIfRevoked,
    currentQuestion,
    currentQuestionIndex,
    isStreaming,
    currentQuestionAnswered,
    assessmentId,
    coachingEnabled,
    getToken,
    setAnswer,
    appendEntry,
    beginStreamingEntry,
    appendStreamingChunk,
    finalizeStreamingEntry,
    markQuestionAnswered,
    setThreadId,
    registerCanceller,
    showCoachError,
    setStreaming,
    toast,
  ]);

  const handleSubmit = useCallback(
    () => runEvaluate(currentAnswer, AssessmentInputMode.TEXT),
    [runEvaluate, currentAnswer]
  );

  const handleAnswerCaptured = useCallback(
    (transcript: string, turnTimings: VoiceTurnTiming[]) => {
      runEvaluate(transcript, AssessmentInputMode.VOICE, turnTimings);
    },
    [runEvaluate]
  );

  const handleFollowUp = useCallback(
    (message: string) => {
      if (!message.trim() || isStreaming || !assessmentId) return;

      appendEntry(currentQuestionIndex, {
        role: ConversationRole.Candidate,
        content: message,
        isStreaming: false,
        occurredAtMs: Date.now(),
      });
      const currentThreadId = threadIds[currentQuestionIndex];

      const replyId = window.setTimeout(async () => {
        beginStreamingEntry(currentQuestionIndex);

        try {
          const token = await getToken();
          if (!token) {
            finalizeStreamingEntry(currentQuestionIndex);
            return;
          }
          const service = new AgentService(token);

          await service.streamChat(
            {
              message,
              competencyId: currentQuestion.competencyId,
              questionType: currentQuestion.questionType,
              threadId: currentThreadId || undefined,
              assessmentId,
              interviewTemplateItemId: currentQuestion.id,
            },
            {
              signal: abortRef.current?.signal,
              onEvent: (event: StreamEvent) => {
                if (event.type === StreamEventType.Chunk) {
                  appendStreamingChunk(currentQuestionIndex, event.text);
                }
              },
              onError: (err) => {
                if (handleIfRevoked(err)) return;
                showCoachError(currentQuestionIndex, err.message);
              },
              onComplete: () => finalizeStreamingEntry(currentQuestionIndex),
            }
          );
        } catch (err) {
          if (abortRef.current?.signal.aborted) return;
          if (handleIfRevoked(err)) return;
          showCoachError(
            currentQuestionIndex,
            err instanceof Error ? err.message : 'Something went wrong'
          );
        }
      }, FOLLOW_UP_DELAY_MS);

      registerCanceller(() => window.clearTimeout(replyId));
    },
    [
      handleIfRevoked,
      currentQuestion,
      currentQuestionIndex,
      isStreaming,
      assessmentId,
      threadIds,
      getToken,
      appendEntry,
      beginStreamingEntry,
      appendStreamingChunk,
      finalizeStreamingEntry,
      showCoachError,
      registerCanceller,
    ]
  );

  const handleModeChange = useCallback(
    (mode: AnswerMode) => {
      if (mode === AnswerMode.Text && currentAnswerMode === AnswerMode.Voice && !currentQuestionAnswered) {
        const partial = currentConversation
          .filter(
            (e) => e.kind === ConversationEntryKind.Answer && e.role === ConversationRole.Candidate
          )
          .map((e) => e.content)
          .join(VOICE_TURN_SEPARATOR);
        if (partial.trim().length > 0) setAnswer(currentQuestionIndex, partial);
      }
      setAnswerMode(currentQuestionIndex, mode);
    },
    [currentAnswerMode, currentQuestionAnswered, currentConversation, currentQuestionIndex, setAnswer, setAnswerMode]
  );

  const handleConversationChange = useCallback(
    (entries: ConversationEntry[]) => setConversation(currentQuestionIndex, entries),
    [currentQuestionIndex, setConversation]
  );

  const confirmExitTo = useCallback((destination: string) => {
    setExitDestination(destination);
  }, []);

  const handleConfirmedExit = useCallback(() => {
    abortRef.current?.abort();
    reset();
    navigate(exitDestination as string);
  }, [navigate, reset, exitDestination]);

  const handleExit = () => confirmExitTo(sessionRoutes.hub);

  usePageNavigationGuard(confirmExitTo);

  const voiceWaitingOnFeedback = VOICE_WAITING_PHASES.has(voicePhase);

  const nextSlotLoading =
    !currentQuestionAnswered &&
    (isVoiceMode
      ? coachingEnabled
        ? voiceAnswerJustCaptured && !voiceWaitingOnFeedback && feedbackAudioSettled
        : voiceAnsweredPhase
      : !coachingEnabled && isStreaming);

  const isVoiceRecordingLive =
    voicePhase === VoicePhase.Connecting ||
    voicePhase === VoicePhase.Reconnecting ||
    RECORDING_PHASES.has(voicePhase);
  const isVoiceCoachSpeaking = voiceActivity === VoiceActivity.Speaking;
  const voiceOrbCondition = voicePhase === VoicePhase.Error ? VoiceOrbCondition.Error : null;
  const modeSwitchDisabled = isVoiceMode
    ? // A muted/unplugged mic or failing transcription is still a recording
      // phase, but typing instead has to work.
      (isVoiceRecordingLive && !voiceInputTrouble) ||
      voiceWaitingOnFeedback ||
      isVoiceCoachSpeaking ||
      // Named directly rather than left to isVoiceCoachSpeaking: that only
      // turns true once the coach is audibly speaking, leaving the gap between
      // the opening request going out and its first audio arriving. Switching
      // to text in that window would tear the connection down between the
      // question and the answer.
      voicePhase === VoicePhase.Asking ||
      (!coachingEnabled && voiceAnsweredPhase)
    : isStreaming || (!coachingEnabled && currentQuestionAnswered);

  const botResponding = isVoiceMode ? isVoiceCoachSpeaking || voiceWaitingOnFeedback : isStreaming;

  const nextCardEligible =
    evaluateFailed ||
    (isVoiceMode
      ? (voiceAnswerJustCaptured || currentQuestionAnswered) && !voiceWaitingOnFeedback
      : currentQuestionAnswered);

  useEffect(() => {
    if (!nextCardRevealed && nextCardEligible && !isStreaming) setNextCardRevealed(true);
  }, [nextCardRevealed, nextCardEligible, isStreaming]);

  const scoringSlot = nextSlotLoading && <ScoringIndicator />;

  const headerPercent = questions.length > 0 ? Math.round((completedIndices.length / questions.length) * 100) : 0;

  usePageHeaderContent(
    <div className={styles.topbarContent}>
      <div className={styles.topbarColored}>
        <div className={styles.topbarMain}>
          <div className={styles.topbarBrand}>
            <Logo type="lettertype" mode="light" className={styles.topbarLogo} />
            <span className={styles.topbarDivider} aria-hidden="true" />
            <span className={styles.topbarPageName}>{templateName ?? 'Interview'}</span>
          </div>

          <div className={styles.topbarRight}>
            <div className={styles.modeToggleWrap}>
              {isDesktop && <Text className={styles.modeToggleLabel}>Session mode</Text>}
              <AnswerModeToggle
                value={currentAnswerMode}
                onChange={handleModeChange}
                disabled={modeSwitchDisabled}
              />
            </div>
            <span className={styles.vdiv} aria-hidden="true" />

            <motion.button
              type="button"
              ref={exitGlassRef}
              className={`${styles.endSessionBtn} ${isVoiceMode ? styles.endSessionBtnEmphasis : ''}`}
              onClick={handleExit}
            >
              {!isMobile && <DoorOpen style={{ width: '1em', height: '1em' }} />}
              <span>Exit</span>
            </motion.button>
          </div>
        </div>

        <div
          className={styles.topbarProgressTrack}
          role="progressbar"
          aria-valuenow={headerPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${headerPercent}% complete`}
        >
          <div className={styles.topbarProgressFill} style={{ width: `${headerPercent}%` }} />
        </div>
      </div>

      <div className={styles.topbarMeta}>
        <span className={styles.topbarMetaText}>
          {isMobile
            ? `Q${currentQuestionIndex + 1} of ${questions.length}`
            : `Question ${currentQuestionIndex + 1} of ${questions.length}`}
        </span>
        <span className={styles.timerMeta}>
          {isVoiceMode && showAnswerTimer && (
            <span className={styles.timerPill}>
              <span className={styles.timerDot} aria-hidden="true" />
              {formatTime(answerElapsedSec)}
            </span>
          )}
        </span>
        <span className={styles.topbarMetaText}>
          {isMobile ? `${headerPercent}%` : `${headerPercent}% complete`}
        </span>
      </div>
    </div>
  );

  if (!currentQuestion) return null;

  return (
    <motion.div
      className={styles.page}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className={styles.bgGlow} aria-hidden="true" />
      {!isVoiceMode && <div className={styles.headerBlurOverlay} aria-hidden="true" />}

      <motion.div
        key={isVoiceMode ? 'voice' : 'text'}
        className={`${styles.scrollArea} ${nextCardRevealed ? styles.scrollAreaReserveNextCard : ''}`}
        style={
          nextCardRevealed
            ? ({ '--next-card-space': `${nextCardHeight}px` } as React.CSSProperties)
            : undefined
        }
        ref={scrollAreaRef}
        onScroll={handleScroll}
        variants={itemVariants}
        initial="hidden"
        animate="visible"
      >
        <div className={`${styles.interview} ${isVoiceMode ? styles.interviewVoice : ''}`}>
          {isVoiceMode && (
            <motion.div className={styles.voiceOrbLift} variants={itemVariants}>
              <Text className={styles.voiceOrbLabel}>
                {voiceActivity === VoiceActivity.Listening
                  ? 'Listening…'
                  : voiceActivity === VoiceActivity.Speaking
                    ? 'Speaking…'
                    : ''}
              </Text>
              <div className={styles.voiceOrbStack}>
                <Suspense fallback={<div className={styles.voiceOrbSkeleton} />}>
                  <VoiceOrb
                    state={voiceActivity}
                    condition={voiceOrbCondition}
                    size={isMobile ? VOICE_ORB_SIZE_MOBILE : VOICE_ORB_SIZE}
                    getAudioLevel={getVoiceOrbAudioLevel}
                    onReady={() => setVoiceOrbReady(true)}
                  />
                </Suspense>
                {!voiceOrbReady && <div className={styles.voiceOrbSkeletonOverlay} />}
              </div>
            </motion.div>
          )}

          <div ref={questionCardRef}>
            <QuestionCard
              question={currentQuestion}
              questionIndex={currentQuestionIndex}
              clamped={isVoiceMode}
            />
          </div>

          {/* Set once, up front: the whole attempt is answered before anything
              is scored or can be redone (see NextStepCard for the rest). */}
          {currentQuestionIndex === 0 && !currentQuestionAnswered && (
            <Text className={styles.attemptHint}>
              {isGuest
                ? 'Answer every question to complete the interview.'
                : 'Answer every question to unlock your scores and the option to redo the ones you pick.'}
            </Text>
          )}

          {showFeedback && (
            <FeedbackThread entries={currentConversation} coachingEnabled={coachingEnabled} />
          )}
        </div>
      </motion.div>

      <div className={styles.inputDock}>
        {!isVoiceMode && !isQuestionInView && (
          <div className={styles.inputDockStart}>
            <div className={styles.questionPeek}>
              <AnimatePresence>
                {showQuestionPeek && (
                  <motion.div
                    className={styles.questionPeekCard}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Text className={styles.questionPeekText}>{currentQuestion.text}</Text>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className={styles.questionPeekToggle}>
                <Text className={styles.questionPeekLabel}>See Question</Text>
                <Switch checked={showQuestionPeek} onChange={setShowQuestionPeek} />
              </div>
            </div>
          </div>
        )}

        <div className={styles.inputDockCenter}>
          {!isVoiceMode && !isAtBottom && currentConversation.length > 0 && (
            <button
              type="button"
              className={`${styles.scrollToBottomBtn} ${nextCardRevealed ? styles.scrollToBottomBtnLifted : ''}`}
              onClick={scrollToBottom}
              aria-label="Scroll to latest message"
              style={
                nextCardRevealed
                  ? ({ '--next-card-lift': `${nextCardHeight}px` } as React.CSSProperties)
                  : undefined
              }
            >
              <ArrowDown style={{ width: '1em', height: '1em' }} />
            </button>
          )}

          <AnimatePresence>
            {nextCardRevealed && (
              <NextStepCard
                key="next-step-card"
                variant={isLastQuestion || allDone ? 'final' : 'next'}
                nextQuestionNumber={currentQuestionIndex + 2}
                totalQuestions={questions.length}
                disabled={botResponding}
                onClick={handleNext}
                onHeightChange={setNextCardHeight}
                isVoiceMode={isVoiceMode}
                isGuest={isGuest}
                coachingEnabled={coachingEnabled}
                canRetake={!isGuest}
              />
            )}
          </AnimatePresence>

          {showAnswerInput ? (
            <AnswerInput
              value={currentAnswer}
              onChange={(val) => setAnswer(currentQuestionIndex, val)}
              onSubmit={handleSubmit}
              disabled={false}
            />
          ) : isVoiceMode ? (
            <div ref={setDockedVoiceSlot} className={styles.voiceDockSlot} />
          ) : (
            showFeedback && coachingEnabled && <FollowUpInput canChat={canChat} onSend={handleFollowUp} />
          )}
        </div>

        {scoringSlot && <div className={styles.inputDockEnd}>{scoringSlot}</div>}
      </div>

      {isVoiceMode &&
        dockedVoiceSlot &&
        createPortal(
          <VoiceAnswerPanel
            key={currentQuestionIndex}
            assessmentId={assessmentId ?? ''}
            interviewTemplateItemId={currentQuestion.id}
            disabled={isStreaming}
            coachingEnabled={coachingEnabled}
            questionType={currentQuestion.questionType}
            alreadyAnswered={currentQuestionAnswered}
            conversation={currentConversation}
            onConversationChange={handleConversationChange}
            onAnswerCaptured={handleAnswerCaptured}
            onRevoked={handleIfRevoked}
            onPhaseChange={setVoicePhase}
            onActivityChange={setVoiceActivity}
            onInputTroubleChange={setVoiceInputTrouble}
            onAudioLevelSource={handleAudioLevelSource}
            onSessionContextSink={handleSessionContextSink}
          />,
          dockedVoiceSlot
        )}

      {exitDestination !== null && (
        <ExitSessionModal
          onStay={() => setExitDestination(null)}
          onSaveAndExit={() => {
            handleConfirmedExit();
            setExitDestination(null);
          }}
        />
      )}
    </motion.div>
  );
};
